import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import { Container, CssBaseline, Divider, useColorScheme } from "@mui/material";
import { parseFixMessages } from "./utils/parser";
import type { FixMessage, ParseResult } from "./domain";
import { ViewMode } from "./domain";
import { isIphone } from "./utils/device";
import type { SetURLSearchParams } from "react-router";
import { useSearchParams } from "react-router";
import { Header } from "./components/Header";
import { ConsentBanner } from "./components/ConsentBanner";
import {
  type ColumnSpec,
  DEFAULT_COLUMNS,
} from "./components/settings/columnSpec";
import type { DataDictionary } from "./utils/data-dictionary";
import { newDataDictionary } from "./utils/data-dictionary";
import type { StoredDictionary } from "./utils/dictionary-storage";
import { buildDataDictionary } from "./utils/dictionary-storage";
import { subscribeToDictionaryChanges } from "./utils/dictionary-broadcast";
import { DictionaryRemovedDialog } from "./components/settings/DictionaryRemovedDialog";
import { LazyFixInput } from "./components/input/LazyFixInput";
import { LazyFixParseResults } from "./components/view/LazyFixParseResults";
import {
  getInitialTitle,
  onDataUpdate,
  onSearchParamsChange,
} from "./utils/memory";
import type {
  GridStateMap,
  GridState,
  TimelineUrlState,
} from "./utils/memory-schema";
import type { GridId } from "./components/grid/gridIds";

const readCurrentSearchParams = () => {
  if (typeof window === "undefined") return undefined;
  const hashQueryStart = window.location.hash.indexOf("?");
  if (hashQueryStart >= 0) {
    return new URLSearchParams(window.location.hash.slice(hashQueryStart + 1));
  }
  return new URLSearchParams(window.location.search);
};

const MainApp = () => {
  const [searchParams, setSearchParams]: [URLSearchParams, SetURLSearchParams] =
    useSearchParams();
  const [fixValue, setFixValue] = React.useState<string>("");
  const [fixMessages, setFixMessages] = useState<ParseResult<FixMessage>[]>([]);
  const { mode } = useColorScheme();
  const [columns, setColumns] = useState<ColumnSpec[]>([...DEFAULT_COLUMNS]);
  const [dataDictionary, setDataDictionary] =
    useState<DataDictionary>(newDataDictionary());
  const [splitPaneLeftPct, setSplitPaneLeftPct] = useState<number | undefined>(
    undefined,
  );
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.VIEW);
  // The Code Lens is hidden on the iPhone (see ViewModeToggle). Coerce any
  // attempt to enter CODE there — including a shared/persisted URL restoring it
  // during hydration — back to the default VIEW so the lens can never mount.
  const setViewModeSafe = useCallback<
    React.Dispatch<React.SetStateAction<ViewMode>>
  >((value) => {
    setViewMode((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      return next === ViewMode.CODE && isIphone() ? ViewMode.VIEW : next;
    });
  }, []);
  const [selectedRowIndices, setSelectedRowIndices] = useState<number[]>([]);
  const [gridStates, setGridStates] = useState<GridStateMap>({});
  const [excludeDateFromMessageTime, setExcludeDateFromMessageTime] =
    useState<boolean>(false);
  const [timelineState, setTimelineState] = useState<
    TimelineUrlState | undefined
  >(undefined);
  const [codeState, setCodeState] = useState<string | undefined>(undefined);
  const [memoryError, setMemoryError] = useState<string | undefined>(undefined);
  const [removedActiveDict, setRemovedActiveDict] = useState<
    StoredDictionary | undefined
  >(undefined);

  // Content-comparing setters: URL deserialization always produces fresh
  // array references, which React's Object.is bailout doesn't catch. Without
  // these, every URL writeback cascades through `[..., columns,
  // selectedRowIndices]` effects and re-parses fixMessages, which tears down
  // the AG Grid rows the user is mid-click on (lost clicks on WebKit).
  const setColumnsStable = useCallback((next: ColumnSpec[]) => {
    setColumns((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    );
  }, []);
  const setSelectedRowIndicesStable = useCallback((next: number[]) => {
    setSelectedRowIndices((prev) => {
      if (prev.length !== next.length) return next;
      for (let i = 0; i < prev.length; i++)
        if (prev[i] !== next[i]) return next;
      return prev;
    });
  }, []);
  const setGridStatesStable = useCallback((next: GridStateMap) => {
    setGridStates((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    );
  }, []);
  const setTimelineStateStable = useCallback(
    (next: TimelineUrlState | undefined) => {
      setTimelineState((prev) =>
        JSON.stringify(prev ?? null) === JSON.stringify(next ?? null)
          ? prev
          : next,
      );
    },
    [],
  );
  const setGridState = useCallback(
    (id: GridId, state: GridState | undefined) => {
      setGridStates((prev) => {
        const nextMap: GridStateMap = { ...prev };
        if (state === undefined) {
          if (!(id in nextMap)) return prev;
          delete nextMap[id];
        } else {
          if (
            JSON.stringify(prev[id] ?? null) === JSON.stringify(state ?? null)
          )
            return prev;
          nextMap[id] = state;
        }
        return nextMap;
      });
    },
    [],
  );
  const dataDictionaryRef = React.useRef(dataDictionary);
  React.useEffect(() => {
    dataDictionaryRef.current = dataDictionary;
  }, [dataDictionary]);

  useEffect(() => {
    return subscribeToDictionaryChanges((msg) => {
      const current = dataDictionaryRef.current;
      if (
        msg.type === "removed" &&
        current.id !== undefined &&
        current.id === msg.id &&
        current.dataSource?.content
      ) {
        setRemovedActiveDict({
          id: current.id,
          filename: current.dataSource.filename,
          content: current.dataSource.content,
          savedAt: Date.now(),
        });
      } else if (msg.type === "added") {
        setRemovedActiveDict((prev) =>
          prev && prev.id === msg.id ? undefined : prev,
        );
      }
    });
  }, []);

  const defaultTitleRef = React.useRef<string>(getInitialTitle());
  const [title, setTitle] = React.useState<string>(defaultTitleRef.current);
  const skipNextUrlWriteRef = React.useRef(false);
  const lastWrittenUrlStateRef = React.useRef<string | undefined>(undefined);

  const hydrateFromSearchParams = useCallback(
    (nextSearchParams: URLSearchParams) => {
      const nextUrlState = nextSearchParams.get("state") ?? "";
      skipNextUrlWriteRef.current =
        nextUrlState !== lastWrittenUrlStateRef.current;
      if (skipNextUrlWriteRef.current) {
        lastWrittenUrlStateRef.current = nextUrlState;
      }
      onSearchParamsChange(
        nextSearchParams,
        setFixValue,
        setColumnsStable,
        setTitle,
        defaultTitleRef.current,
        dataDictionaryRef.current.dataSource?.content,
        setDataDictionary,
        setSplitPaneLeftPct,
        setViewModeSafe,
        setSelectedRowIndicesStable,
        setGridStatesStable,
        setExcludeDateFromMessageTime,
        setTimelineStateStable,
        setMemoryError,
        setCodeState,
      );
    },
    [
      setColumnsStable,
      setSelectedRowIndicesStable,
      setGridStatesStable,
      setTimelineStateStable,
      setViewModeSafe,
    ],
  );

  React.useLayoutEffect(() => {
    const actualSearchParams = readCurrentSearchParams();
    if (
      actualSearchParams &&
      (actualSearchParams.get("state") ?? "") !==
        (searchParams.get("state") ?? "")
    ) {
      return;
    }
    hydrateFromSearchParams(searchParams);
  }, [searchParams, hydrateFromSearchParams]);

  useEffect(() => {
    const hydrateCurrentUrl = () => {
      const nextSearchParams = readCurrentSearchParams();
      if (nextSearchParams) hydrateFromSearchParams(nextSearchParams);
    };
    window.addEventListener("hashchange", hydrateCurrentUrl);
    window.addEventListener("popstate", hydrateCurrentUrl);
    return () => {
      window.removeEventListener("hashchange", hydrateCurrentUrl);
      window.removeEventListener("popstate", hydrateCurrentUrl);
    };
  }, [hydrateFromSearchParams]);

  // Parsing only depends on the raw FIX input — keep it in its own effect so
  // unrelated URL-state changes (e.g. gridStates updates from a column
  // filter) don't recreate `fixMessages` and cascade into MessagesGrid's
  // colDefs useMemo (which would force AG Grid to rebuild the header row
  // and close any open filter popup mid-typing).
  useEffect(() => {
    setFixMessages(parseFixMessages(fixValue));
  }, [fixValue]);

  useEffect(() => {
    if (skipNextUrlWriteRef.current) {
      skipNextUrlWriteRef.current = false;
      return;
    }
    const dictionary =
      dataDictionary.enabled && dataDictionary.dataSource?.content
        ? {
            filename: dataDictionary.dataSource.filename,
            content: dataDictionary.dataSource.content,
          }
        : undefined;
    const writtenUrlState = onDataUpdate(
      searchParams,
      setSearchParams,
      fixValue,
      columns,
      title,
      dictionary,
      splitPaneLeftPct,
      viewMode,
      selectedRowIndices,
      gridStates,
      excludeDateFromMessageTime,
      timelineState,
      undefined,
      lastWrittenUrlStateRef.current,
      codeState,
    );
    if (writtenUrlState !== undefined) {
      lastWrittenUrlStateRef.current = writtenUrlState;
    }
  }, [
    fixValue,
    columns,
    title,
    dataDictionary,
    splitPaneLeftPct,
    viewMode,
    selectedRowIndices,
    gridStates,
    excludeDateFromMessageTime,
    timelineState,
    codeState,
  ]);

  useEffect(() => {
    const effectiveTitle = (title || "").trim() || defaultTitleRef.current;
    if (typeof document !== "undefined" && document.title !== effectiveTitle) {
      document.title = effectiveTitle;
    }
  }, [title]);

  const handleSetViewMode: React.Dispatch<React.SetStateAction<ViewMode>> = (
    value,
  ) => {
    setViewModeSafe(value);
    setSelectedRowIndices([]);
  };

  const handleSetDataDictionary: React.Dispatch<
    React.SetStateAction<DataDictionary>
  > = setDataDictionary;

  // A sample may bundle a dictionary; attach it as an external ("url")
  // dictionary — active for the session and shareable via URL, but not written
  // to local storage.
  const handleLoadSampleDictionary = useCallback(
    (dictionary: { filename: string; content: string } | undefined) => {
      if (!dictionary) return;
      setDataDictionary(
        buildDataDictionary(dictionary.filename, dictionary.content, "url"),
      );
    },
    [],
  );

  if (!mode) {
    return null;
  }

  return (
    <>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="static">
          <Header
            title={title}
            onReset={() => {
              setFixValue("");
              setColumns([...DEFAULT_COLUMNS]);
            }}
            onTitleChange={setTitle}
          />
        </AppBar>
      </Box>
      <Container maxWidth="xl">
        <Box sx={{ my: 2 }}>
          <LazyFixInput
            memoryError={memoryError}
            overrideFixValue={fixValue}
            onLoadDictionary={handleLoadSampleDictionary}
            onSubmit={(nextValue: string) => {
              setFixValue(nextValue);
              setMemoryError(undefined);
            }}
          />
        </Box>
      </Container>
      <Divider />
      <LazyFixParseResults
        codeState={codeState}
        columns={columns}
        dataDictionary={dataDictionary}
        excludeDateFromMessageTime={excludeDateFromMessageTime}
        fixMessages={fixMessages}
        gridStates={gridStates}
        selectedRowIndices={selectedRowIndices}
        setCodeState={setCodeState}
        setColumns={setColumns}
        setDataDictionary={handleSetDataDictionary}
        setExcludeDateFromMessageTime={setExcludeDateFromMessageTime}
        setGridState={setGridState}
        setSelectedRowIndices={setSelectedRowIndicesStable}
        setSplitPaneLeftPct={setSplitPaneLeftPct}
        setTimelineState={setTimelineStateStable}
        setViewMode={handleSetViewMode}
        splitPaneLeftPct={splitPaneLeftPct}
        timelineState={timelineState}
        viewMode={viewMode}
      />
      <DictionaryRemovedDialog
        removedDictionary={removedActiveDict}
        onDismiss={() => setRemovedActiveDict(undefined)}
      />
      <ConsentBanner />
    </>
  );
};

export default MainApp;
